
import { useEffect, useMemo, Fragment, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withTheme } from 'styled-components';

import { getTeamStatistics } from 'store/actions/teamData';
import { useCookies } from 'react-cookie';
import { FovDivider, FovLoader, H4, H5, VerticalSpacer, FovOption, FovSelect } from 'components';
import { BarGraph } from './BarGraph';
import { useSearchParams } from 'react-router-dom';
import { SortContainer } from '../styles';

const Analytics = withTheme(() => {
    const dispatch = useDispatch();

    const teamDataStore = useSelector(state => state.teamData);
    const userDataStore = useSelector(state => state.userData);
    const searchFieldRef = useRef('');

    const [ cookies ] = useCookies([ 'userAuth' ]);
    const token = cookies.archvision_token;

    const selectedTheme = userDataStore.userPreferences.theme;
    const teamId = useMemo(() => teamDataStore.teamId, [ teamDataStore.teamId ]);
    const selectedTeamId = useMemo(() => teamDataStore.selectedTeamId, [ teamDataStore.selectedTeamId ]);
    const teamDataIsLoading = useMemo(() => teamDataStore.isLoading, [ teamDataStore.isLoading ]);
    const teamStatistics = useMemo(() => teamDataStore.statistics, [ teamDataStore.statistics ]);

    const [ searchParams, setSearchParams ] = useSearchParams();

    const teamJobStats = teamStatistics?.teamJobsStats.toSorted((a,b) => {
        if (a.year < b.year) return -1;
        if (a.year > b.year) return 1;
        if (a.year === b.year) {
            if (a.month < b.month) return -1;
            if (a.month > b.month) return 1;
        }
        return 0;
    });
    const teamDownloadStats = teamStatistics?.teamDownloadStats.toSorted((a,b) => {
        if (a.year < b.year) return -1;
        if (a.year > b.year) return 1;
        if (a.year === b.year) {
            if (a.month < b.month) return -1;
            if (a.month > b.month) return 1;
        }
        return 0;
    });

    useEffect(() => {
        if (selectedTeamId !== teamId) {
            dispatch(getTeamStatistics(token, selectedTeamId));
        }
    }, [ teamId, selectedTeamId ]);

    useEffect(() => {
        if (!searchParams.has('search') && searchFieldRef.current) {
            searchFieldRef.current.value = '';
        }
    }, [ searchParams ]);

    const timeOptions = [
            { key: 'time_frame_3', text: 'Last 3 Months', value: 3 },
            { key: 'time_frame_6', text: 'Last 6 Months', value: 6 },
            { key: 'time_frame_12', text: 'Last Year', value: 12 },
        ];
    const buildTimeOptions = () => timeOptions.map(option => (
        <FovOption key={option.key} value={option.value}>
            {option.text}
        </FovOption>
    ));

    return (
        <FovLoader
            text='Retrieve team analytics'
            resource={!teamDataIsLoading}
        >
            {!teamStatistics && <div>No team stats available.</div>}
            {teamStatistics && (
                <Fragment>
                    <SortContainer>
                        <FovSelect
                            value={searchParams.get('time_frame') || ''}
                            placeholder={'Retrieve from'}
                            onChange={(e,newVal) => {
                                if (!searchParams.has('time_frame') || (searchParams.get('time_frame') !== newVal)) {
                                    searchParams.set('time_frame', newVal);
                                    setSearchParams(searchParams);
                                    dispatch(getTeamStatistics(token, selectedTeamId));
                                }
                            }}
                        >
                            {buildTimeOptions()}
                        </FovSelect>
                    </SortContainer>
                    <H4>Team Publishes by Month</H4>
                    <VerticalSpacer size={8} />
                    <BarGraph $selectedTheme={selectedTheme} data={teamJobStats} />
                    <FovDivider />
                    <VerticalSpacer size={10} />
                    <H4>Team Downloads by Month</H4>
                    <VerticalSpacer size={8} />
                    <BarGraph $selectedTheme={selectedTheme} data={teamDownloadStats} />
                    <FovDivider />
                    <VerticalSpacer size={10} />
                    <H4>Team Total Models</H4>
                    <H5>{teamStatistics.teamModelCount}</H5>
                </Fragment>
            )}
        </FovLoader>
    );

});

export { Analytics };